'use client';

import { useContext, ContextType, useRef, useState, useEffect, useMemo } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from '@/navigation';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { VisibilityContext } from 'react-horizontal-scrolling-menu';
import clsx from 'clsx';
import { css } from 'styled-system/css';
import { ViewEnum } from '@/types';
import { NavItemType } from '@/types/nav';
import { useTopHeaderDisplay } from '@/hooks/use-top-header-display';
import { useMenuTypeList } from '@/hooks/query/use-menu-data';
import { useAccountStatus } from '@/hooks/query/account';
import { useSportHref } from '@/hooks/menu/use-sport-href';
import { useGetSiteConfigs, useGetSiteConfigsWithSession } from '@/hooks/query/use-site-configs';
import { TopMenuItem } from './components/top-menu-item';
import { Icon } from '@/components/icons/svg-icons';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote, PromotionType } from '@nf/utils-common/constants';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useSiteStore } from '@/store/site';
import { useAppearanceStore } from '@/store/appearance';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { sportsbookService } from '@/services/sportsbook.service';
import { menuDefinition } from '@/types/menu-setting';
import { topMenuStyle } from './top-menu.style';
import { Tooltip } from '@/components/widgets/tooltip';
import { tooltip } from 'styled-system/recipes';
import { modalService } from '@/services/modal.service';
import { goldenDuelStorageService } from '@/services/golden-duel/golden-duel-storage.service';
import { useSectionSportsContributor } from '@/hooks/query/use-section-sports';
import { phSectionSports } from '@/settings/sports-settings';
import { useAtomValue } from 'jotai';
import { isStickyBarCollapsedAtom } from '@/atoms/sticky';
import { Button } from '@nf/ui';
import { uiService } from '@/services/ui.service';
import { MyBetsButton } from '@/components/feature-buttons/my-bets-button';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { useCurrentProperties } from '@/hooks/query';
import { sportService } from '@/services/sport.service';

const ScrollMenu = dynamic(() => import('react-horizontal-scrolling-menu').then(mod => mod.ScrollMenu), { ssr: false });

export type NavItems = Array<{
	type: NavItemType;
	href: string;
	icon?: string;
	label?: string;
	className?: string;
	needAuth?: boolean;
	needPromotion?: boolean;
	forSampleSite?: boolean;
}>;
type NavItem = NavItems[0];

type scrollVisibilityApiType = ContextType<typeof VisibilityContext>;

export const TopMenu = () => {
	const { filterSabaString } = useFilterSabaString();
	const router = useRouter();
	const locale = useLocale();
	const view = useAppearanceStore(state => state.view);
	const isGalaxyView = view === ViewEnum.galaxy;
	const [
		resetAllFilter,
		setCurrentPage,
	] = useSiteStore(state => [
		state.resetAllFilter,
		state.setCurrentPage,
	]);
	const { resetSwitcher } = useOddsPageSwitcher();
	const t = useTranslations('General');
	const { data: actStatus } = useAccountStatus();
	const canDisplayTopHeader = useTopHeaderDisplay();
	const { createSportHref } = useSportHref();
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const isPromotionPage = selectedLayoutSegments.includes('promotion');
	const isStreamerPage = selectedLayoutSegments.includes('streamer');
	const isBefore = actStatus === undefined;
	const { SiteConfigsData } = useGetSiteConfigs();
	const { SiteConfigsWithSessionData } = useGetSiteConfigsWithSession();
	const { menuTypeList } = useMenuTypeList();
	const order = Object.keys(menuDefinition);
	const [showGoldenDuelTooltip, setShowGoldenDuelTooltip] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const { sport } = useCurrentProperties();
	const isAnyVirtualSports = sportService.isAnyVirtualSportsProduct(sport);

	const sortedMenuTypeList = menuTypeList
		.filter(item => item.modeId > 0)
		.sort((a, b) => {
			return order.indexOf(`M${a.modeId}`) - order.indexOf(`M${b.modeId}`)
		});

	const menuTypeItems: NavItems =
		sortedMenuTypeList.map(item => {
			const labelKey = sportsbookService.makeMenuNodeResourceKey(
				SiteConfigsData?.EnableFilterSaba,
				item.modeId.toString()
			);

			return {
				type: NavItemType.World,
				label: t(labelKey),
				href: item.path,
			};
		});

	const apiRef = useRef({} as scrollVisibilityApiType);
	const { data: sectionData } = useSectionSportsContributor(
		SiteConfigsData?.DirectApiUrl ?? '', 
		phSectionSports, 
		'PHL'
	);

	const isStickyBarCollapsed = useAtomValue(isStickyBarCollapsedAtom);

	const sectionTotalCount = useMemo(() => {
		if (!sectionData?.data?.marketType) return 0;
		
		const marketType = sectionData.data.marketType;

		return Object
			.values(marketType)
			.reduce((sum, count) => sum + (count || 0), 0);
	}, [sectionData]);

	let menuItems = [
		...createFixedMenuItems(t),
		...menuTypeItems
	];

	if (actStatus !== 2) {
		menuItems = menuItems.filter(item => !item.needAuth);
	}

	if (!SiteConfigsData?.EnableLicLobby || isBefore) {
		menuItems = menuItems.filter(item => item.type !== NavItemType.Lobby);
	}

	if (!SiteConfigsData?.EnableMyFavorite) {
		menuItems = menuItems.filter(item => item.type !== NavItemType.Favorites);
	}

	if (SiteConfigsData?.EnableFilterEvent) {
		menuItems = menuItems.filter(item => item.type !== NavItemType.Search);
	}

	if (!SiteConfigsWithSessionData?.EnablePhPortal || !isGalaxyView || sectionTotalCount === 0) {
		menuItems = menuItems.filter(item => item.type !== NavItemType.PhilippinesSection);
	}

	if (isGalaxyView && SiteConfigsData?.EnableHeadlessForGalaxy) {
		menuItems = menuItems.filter(item => item.type !== NavItemType.Lobby && item.type !== NavItemType.Promotion);
	}
	// only support king back in galaxy view
	if (!SiteConfigsData?.EnableSportEvent || !isGalaxyView) {
		menuItems = menuItems.filter(item => item.type !== NavItemType.SportEvent);
	}

	const showHomeIcon = (SiteConfigsData?.EnableLicLobby && !isBefore)
	const showPromotionIcon = SiteConfigsData?.EnablePromotion ?? 0 > PromotionType.Close;

	if (isPromotionPage) {
		menuItems = showHomeIcon ? menuItems.filter(item => item.type !== NavItemType.Promotion) : menuItems.filter(item => item.type !== NavItemType.Lobby)
	} else {
		menuItems = showPromotionIcon ? menuItems.filter(item => item.type !== NavItemType.Lobby) : menuItems.filter(item => item.type !== NavItemType.Promotion)
	}

	const handleItemClick = (item: NavItem) => () => {
		const action = {
			[NavItemType.Lobby]: 9,
			[NavItemType.Search]: 1,
			[NavItemType.Favorites]: 2,
			[NavItemType.Live]: 3,
			[NavItemType.Sports]: 4,
			[NavItemType.World]: 6,
			[NavItemType.Parlay]: undefined,
			[NavItemType.Promotion]: 10,
			[NavItemType.PhilippinesSection]: 5,
			[NavItemType.SportEvent]: 5 
		}[item.type];

		if (item.type === NavItemType.SportEvent && action) {
			setKafkaNormal({
				note: KafkaNote.KingBack,
				type: 1,
				action
			});
		} else if (action) {
			setKafkaNormal({
				note: KafkaNote.GalaxyV2,
				type: 2,
				action
			});
		}
		
		if (window.innerWidth < 750) {
			if (item.href.includes('/world/world-cup')) {
				setKafkaNormal({
					note: KafkaNote.GalaxyV2,
					type: 2,
					action: 11
				});
			}
			if (item.href.includes('saba-virtual-fifa-world-cup')) {
				setKafkaNormal({
					note: KafkaNote.GalaxyV2,
					type: 2,
					action: 14
				});
			}
			if (item.href.includes('fun88-virtual-fifa-world-cup')) {
				setKafkaNormal({
					note: KafkaNote.GalaxyV2,
					type: 2,
					action: 15
				});
			}
			if (item.href.includes('m88-virtual-fifa-world-cup')) {
				setKafkaNormal({
					note: KafkaNote.GalaxyV2,
					type: 2,
					action: 16
				});
			}
		}
		if (item.type === NavItemType.Live || item.type === NavItemType.Sports) {
			setCurrentPage('lbl_Sports');
		}
		

		resetAllFilter();
		resetSwitcher();
		const href = ['/promotion', '/lobby', '/search', '/favorites', '/section/ph', '/section/sport-event'].includes(item.href) ? item.href : createSportHref(item.href);
		router.push(href, { locale });
	};

	useEffect(() => {
		const subscription = modalService.onOpenGoldenDuelToolTipModal.subscribe((isOpen) => {
			if (SiteConfigsData?.EnableGoldenDuel) {
				setShowGoldenDuelTooltip(isOpen);
			}
		});

		return () => subscription.unsubscribe();
	}, []);

	useEffect(() => {
		const subscription = uiService.onControlSidebarButton$.subscribe(shouldOpenSidebar => {
			setIsSidebarOpen(shouldOpenSidebar);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);


	return (
		<>
			<div
				className={
					clsx('top-nav', {
						'display-none': !canDisplayTopHeader,
						'top-nav--virtual': isAnyVirtualSports,

					})
				}
				data-has-menutype={menuTypeItems.length > 0}
				data-sticky-bar-collapsed={isStickyBarCollapsed && !SiteConfigsData?.EnableHeadlessForGalaxy}
			>
				{ isGalaxyView && SiteConfigsData?.EnableHeadlessForGalaxy ? (
					<>
						<Button
							variant="icon"
							className="top-nav__explore-menu"
							style={{ flex: '0 0 auto' }}
							onClick={() => uiService.controlSidebar(true)}
						>
							{isSidebarOpen ? <Icon name="close-big" /> : <Icon name="explore-menu" />}
						</Button>
						{actStatus === 2 ? <MyBetsButton from="top-menu" /> : null}
					</>
				) : null}
				<ScrollMenu
					wrapperClassName={clsx('tab tab--pills')}
					scrollContainerClassName="tab__container"
					LeftArrow={LeftArrow}
					RightArrow={RightArrow}
					apiRef={apiRef}
				>
					{menuItems?.map((item, idx) => (
						<TopMenuItem
							key={idx}
							href={item.href}
							className={
								clsx('tab__item', item.className, 
									{ 
										'sport-event__top-nav': item.type === NavItemType.SportEvent,
										'sport-event__top-nav-desktop': item.type === NavItemType.SportEvent && isGalaxyView
									}
								)
							}
							type={item.type}
							onClick={handleItemClick(item)}
						>
							{item.icon ? (
								<Icon name={item.icon} />
							) : (
								<div className={clsx('tab__text', { 'tab__text--with-icon': item.type === NavItemType.PhilippinesSection })}>
									{item.type === NavItemType.PhilippinesSection ? (
										<Image className="top-nav-img-icon" src="/images/common/philippines.svg" alt="" width={18} height={18} />
									) : null}
									{item.type === NavItemType.SportEvent ? (
										<>
											<Image className="sport-event__entry-icon" src="/images/sport-event/entry-icon-mobile.png" alt="" width={17} height={17} />
											<Image className="top-nav-img-icon" src="/images/common/kingback.svg" alt="" width={18} height={18} />
										</>
									) : null}
									{filterSabaString(item.label ?? '')}
								</div>
							)}
						</TopMenuItem>
					))}
				</ScrollMenu>
				{
					isGalaxyView && SiteConfigsData?.EnableHeadlessForGalaxy
						? null
						: (
							<TopMenuItem
								href={'/search'}
								className="top-nav-search-btn"
								type={NavItemType.Search}
								onClick={handleItemClick({
									type: NavItemType.Search,
									href: '/search',
									icon: 'search'
								})}
							>
								<Icon name="search" />
							</TopMenuItem>
						)
				}
			</div>
			{showGoldenDuelTooltip && view !== ViewEnum.mars ? (
				<Tooltip
					className={clsx(tooltip({ horizontal: 'left', size: 'sm' }), topMenuStyle.tip, 'tip')}
					message={t('lbl_LotteryLocation')}
					countdownTime={5000}
					showMessageDoAction={() => goldenDuelStorageService.markTooltipAsShown()}
					clearMessage={() => setShowGoldenDuelTooltip(false)}
				/>
			
			) : null}
		</>
	);
};

const createFixedMenuItems = (
	t: ReturnType<typeof useTranslations<'General'>>
): NavItems => [
	{
		type: NavItemType.Promotion,
		href: '/promotion',
		icon: 'giftbox',
		needPromotion: true
	},
	{
		type: NavItemType.Lobby,
		href: '/lobby',
		icon: 'lobby',
	},
	{
		type: NavItemType.Favorites,
		href: '/favorites',
		icon: 'favorite',
		needAuth: true
	},
	{
		type: NavItemType.Live,
		href: '/live',
		label: t('lbl_live'),
		className: 'top-nav-live'
	},
	{
		type: NavItemType.Sports,
		href: '/sports',
		label: t('lbl_SportsParty'),
	},
	{
		type: NavItemType.PhilippinesSection,
		href: '/section/ph',
		label: 'PH',
	},
	{
		type: NavItemType.SportEvent,
		href: '/section/sport-event',
		label: t('lbl_KREntry'),
	},
];

const LeftArrow = () => {
	const { isFirstItemVisible } = useContext(VisibilityContext);
	return (
		<div
			className="tab__mask tab__mask--left"
			data-disabled={isFirstItemVisible}
		/>
	);
};

const RightArrow = () => {
	const { scrollContainer } = useContext(VisibilityContext);

	const isReachedEnd = scrollContainer?.current
		? Math.ceil(scrollContainer.current.scrollLeft + scrollContainer.current.clientWidth)
		>= scrollContainer.current.scrollWidth - 1
		: false;

	return (
		<div
			className="tab__mask tab__mask--right"
			data-disabled={isReachedEnd}
		/>
	);
};
